import { IS_MOBILE, VIDEO_OFF } from '../config';

// videoPool — the GLOBAL concurrent-video governor (promotions spec §7).
//
// Video textures are the one thing that can undo the KTX2/VRAM win: each playing
// clip runs a hardware decoder AND uploads a raw uncompressed frame to the GPU
// every frame (1080p ≈ 8MB per buffer), and mobile browsers cap concurrent
// decoders hard — past the limit they stall, go black, or take the tab down.
//
// So surfaces don't decide for themselves: they REGISTER as candidates with their
// distance + on-screen state, and the pool grants a limited number of slots to the
// nearest on-screen ones. Everyone else shows their still poster frame. Walk
// around and the slots hand over.
//
// Cap: 4 desktop / 3 MOBILE — 3 because the main spawn framing shows three boards
// and a cap of 2 would leave one dead in the hero shot.
export const MAX_VIDEOS = IS_MOBILE ? 3 : 4;

const candidates = new Map(); // id -> { dist, visible, onChange(granted) }
let granted = new Set();
let timer = null;

function recompute() {
  // Stills test (config.VIDEO_OFF): nobody gets a slot — every registered
  // surface falls back to its poster, which is exactly the over-cap behaviour
  // they all already implement.
  const ranked = VIDEO_OFF ? [] : [...candidates.entries()]
    .filter(([, c]) => c.visible && Number.isFinite(c.dist))
    .sort((a, b) => a[1].dist - b[1].dist)
    .slice(0, MAX_VIDEOS)
    .map(([id]) => id);
  const next = new Set(ranked);
  // Notify only what changed, so holders don't tear down every tick.
  for (const id of granted) {
    if (!next.has(id)) candidates.get(id)?.onChange?.(false);
  }
  for (const id of next) {
    if (!granted.has(id)) candidates.get(id)?.onChange?.(true);
  }
  granted = next;
}

function ensureTimer() {
  if (timer || typeof window === 'undefined') return;
  // Re-rank on an interval (not per frame) — slot churn should be lazy, and
  // decoders are expensive to start/stop.
  timer = setInterval(recompute, 700);
}

/** Register a surface that COULD play video. Returns an unregister fn. */
export function registerVideo(id, onChange) {
  candidates.set(id, { dist: Infinity, visible: false, onChange });
  ensureTimer();
  return () => {
    candidates.delete(id);
    if (granted.delete(id)) onChange?.(false);
  };
}

/** Per-frame-ish report of how close/visible this surface is. Cheap. */
export function updateVideo(id, dist, visible) {
  const c = candidates.get(id);
  if (!c) return;
  c.dist = dist;
  c.visible = visible;
}

export function hasSlot(id) { return granted.has(id); }
